import cookie from 'cookie-cutter';
import merge from 'lodash/merge';
import { useEffect, useState } from 'react';
import useFetch from 'use-http';
/**
 * A thin wrapper around the thirdparty useFetch
 * ('use-http' on npm) library.
 *
 * Visit https://use-http.com/ to see detailed documentation.
 *
 * CUSTOMIZATIONS:
 * To work efficiently in the HV Platform, we've added the following
 * defaults that differ from the defaults offered by useFetch directly:
 *
 * - CSRF tokens are included by default to make authenticated calls more easily
 * - The cache policy has been set to 'no-cache' to allow us to easily get
 * updates from endpoints we've already fetched from, which is common in a
 * thin client infrastructure.
 *
 * These settings can be overwritten on instantiation.
 *
 * @param {string} url The url to perform fetch operations on.
 * @param {UseHVFetchOptions} [options] Options for the fetch(es). See https://use-http.com/ for details. See notes above / code below for defaults.
 * @param {any[]} [dependencies] A dependencies array similar to one passed into useEffect. If an empty array is passed in, a GET will be performed onMount. If an array of variables is passed in, it will be performed when those variables change. If nothing is passed in, GET will not be called onMount.
 * @returns {UseFetch} see return value on https://use-http.com/
 */
function useHVFetch(url, options, dependencies) {
    const defaults = {
        isTaskBased: false,
        taskURL: '/api/v2/async/result',
        taskRetryInterval: 2000,
        cachePolicy: 'no-cache',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': cookie.get('csrftoken')
        }
    };
    const [task_id, setTask_ID] = useState(null);
    const settings = merge({}, { ...defaults, cachePolicy: defaults.cachePolicy }, { ...options, cachePolicy: options === null || options === void 0 ? void 0 : options.cachePolicy });
    const { isTaskBased, taskURL, taskRetryInterval } = settings;
    // Delete the props not on standard useFetch to
    // pass type checking
    delete settings.isTaskBased;
    delete settings.taskURL;
    delete settings.taskRetryInterval;
    // Settings to call to the asynchronous task endpoint;
    // We never want to cache, because we need to call it
    // over and over again.
    const taskSettings = {
        cachePolicy: defaults.cachePolicy,
        credentials: defaults.credentials,
        headers: defaults.headers
    };
    // useFetch instantiation for a regular endpoint
    const standardReturn = useFetch(url, settings, dependencies);
    const { data, response, error } = standardReturn;
    // useFetch instantiation for the asynchronous task endpoint
    const taskReturn = useFetch(taskURL, taskSettings);
    const { get: taskGet, data: taskData, response: taskResponse, loading: taskLoading } = taskReturn;
    // Make sure data stays undefined until the final task returns
    // If not manually replaced, it can become empty string
    // or some other falsey value after the first fetch
    // to the result endpoint
    // Limiting it to a single change from 'undefined' to a data
    // or error value makes it easier for the enduser,
    // as well as easier for testing
    // TODO - add E2E testing for task-based fetching once implemented in production
    const taskReturnFinal = {
        ...taskReturn,
        data: !taskData ? undefined : taskData
    };
    const taskResult = () => {
        window.setTimeout(() => {
            taskGet(`${task_id}/`);
        }, taskRetryInterval);
    };
    useEffect(() => {
        // If not an asynchronous task, just return immediately
        if (!isTaskBased)
            return;
        // If an async task and the first fetch has finished successfully
        if (response.status === 200) {
            // If there is a valid task ID
            if (data === null || data === void 0 ? void 0 : data.task_id) {
                setTask_ID(data.task_id);
                taskGet(`${data.task_id}/`);
            }
            else {
                // No valid task ID
                console.warn('The endpoint did not return a task_id. Do you need to set isTaskBased to false?');
            }
        }
    }, [data, response]);
    useEffect(() => {
        // If:
        //  - we're not in an async call at all
        //  - task-based call is still loading
        //  - taskResponse.status is anything but a 204
        // Then break out of the loop
        if (!isTaskBased || taskLoading || taskResponse.status !== 204) {
            return;
        }
        // 204 means the task is still in progress;
        // Check again in {taskRetryInterval} ms
        taskResult();
    }, [taskData, taskLoading]);
    // If the initial fetch errors out, even in the case of an async call, return that
    return !!isTaskBased && !!(data === null || data === void 0 ? void 0 : data.task_id) && !error
        ? taskReturnFinal
        : standardReturn;
}
export default useHVFetch;
